/* ─── Tipos ─── */

export type WordLevel = 'leve' | 'medio' | 'grave';
export type WordAction = 'ocultar' | 'reemplazar' | 'revision' | 'bloquear_temp' | 'banear_ip';

export interface BannedWord {
  id: string;
  word: string;
  level: WordLevel;
  action: WordAction;
  addedAt: string;
  addedBy: string;
  active: boolean;
}

export interface BanEntry {
  id: string;
  username: string;
  ip: string;
  reason: string;
  action: 'bloqueo_temp' | 'bloqueo_perm' | 'baneo_ip';
  date: string;
  duration: string;
  bannedBy: string;
  active: boolean;
}

export interface BlockedIP {
  id: string;
  ip: string;
  reason: string;
  blockedAt: string;
  blockedBy: string;
  notes: string;
  active: boolean;
}

export interface SilencedUser {
  id: string;
  username: string;
  silencedAt: string;
  duration: string;
  silencedBy: string;
  reason: string;
  active: boolean;
}

export interface ModHistoryEntry {
  id: string;
  timestamp: string;
  admin: string;
  action: string;
  target: string;
  targetType: 'mensaje' | 'usuario' | 'ip' | 'palabra' | 'sistema';
  details: string;
}

export interface AutoBlockConfig {
  enabled: boolean;
  warningsBeforeBlock: number;
  blockDurationMinutes: number;
  blockSpam: boolean;
  blockLinks: boolean;
  blockInsults: boolean;
  blockRepeated: boolean;
  repeatedThreshold: number;
}

export interface AutoRule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  icon: string;
  matchCount: number;
}

/* ─── Mock Data ─── */

export const autoBlockDefaults: AutoBlockConfig = {
  enabled: true,
  warningsBeforeBlock: 3,
  blockDurationMinutes: 60,
  blockSpam: true,
  blockLinks: true,
  blockInsults: true,
  blockRepeated: true,
  repeatedThreshold: 5,
};

export const autoRules: AutoRule[] = [
  {
    id: 'rule-1',
    name: 'Malas palabras',
    description: 'Detecta y filtra insultos y lenguaje ofensivo en tiempo real.',
    enabled: true,
    icon: 'ri-emotion-unhappy-line',
    matchCount: 847,
  },
  {
    id: 'rule-2',
    name: 'Ofensas',
    description: 'Identifica ataques personales, discriminación y acoso hacia otros usuarios.',
    enabled: true,
    icon: 'ri-shield-user-line',
    matchCount: 312,
  },
  {
    id: 'rule-3',
    name: 'Spam',
    description: 'Bloquea mensajes repetitivos, floods y contenido no solicitado.',
    enabled: true,
    icon: 'ri-spam-2-line',
    matchCount: 1203,
  },
  {
    id: 'rule-4',
    name: 'Links sospechosos',
    description: 'Detecta enlaces a sitios maliciosos, phishing o contenido no autorizado.',
    enabled: true,
    icon: 'ri-links-line',
    matchCount: 89,
  },
  {
    id: 'rule-5',
    name: 'Mayúsculas excesivas',
    description: 'Filtra mensajes con más del 70% de caracteres en mayúsculas sostenidas.',
    enabled: false,
    icon: 'ri-font-size',
    matchCount: 451,
  },
  {
    id: 'rule-6',
    name: 'Mensajes repetidos',
    description: 'Detecta cuando un usuario envía el mismo mensaje más de 5 veces seguidas.',
    enabled: true,
    icon: 'ri-loop-left-line',
    matchCount: 267,
  },
];

export const bannedWords: BannedWord[] = [
  {
    id: 'bw-1',
    word: 'p***',
    level: 'grave',
    action: 'bloquear_temp',
    addedAt: '2025-11-15T10:30:00Z',
    addedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'bw-2',
    word: 'm****a',
    level: 'grave',
    action: 'bloquear_temp',
    addedAt: '2025-11-15T10:31:00Z',
    addedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'bw-3',
    word: 'c*****',
    level: 'grave',
    action: 'bloquear_temp',
    addedAt: '2025-11-15T10:32:00Z',
    addedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'bw-4',
    word: 'idiota',
    level: 'medio',
    action: 'reemplazar',
    addedAt: '2025-12-01T14:20:00Z',
    addedBy: 'María Solís',
    active: true,
  },
  {
    id: 'bw-5',
    word: 'estúpido',
    level: 'medio',
    action: 'reemplazar',
    addedAt: '2025-12-01T14:21:00Z',
    addedBy: 'María Solís',
    active: true,
  },
  {
    id: 'bw-6',
    word: 'imbécil',
    level: 'medio',
    action: 'revision',
    addedAt: '2025-12-10T09:45:00Z',
    addedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'bw-7',
    word: 'h*******a',
    level: 'grave',
    action: 'bloquear_temp',
    addedAt: '2026-01-05T16:00:00Z',
    addedBy: 'Diego Ramírez',
    active: true,
  },
  {
    id: 'bw-8',
    word: 'tonto',
    level: 'leve',
    action: 'ocultar',
    addedAt: '2026-02-18T11:15:00Z',
    addedBy: 'María Solís',
    active: true,
  },
  {
    id: 'bw-9',
    word: 'payaso',
    level: 'leve',
    action: 'ocultar',
    addedAt: '2026-03-01T08:30:00Z',
    addedBy: 'Diego Ramírez',
    active: false,
  },
  {
    id: 'bw-10',
    word: 'vete al diablo',
    level: 'medio',
    action: 'revision',
    addedAt: '2026-03-22T13:20:00Z',
    addedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'bw-11',
    word: 'maldito',
    level: 'leve',
    action: 'reemplazar',
    addedAt: '2026-04-05T10:10:00Z',
    addedBy: 'María Solís',
    active: true,
  },
  {
    id: 'bw-12',
    word: 'muérete',
    level: 'grave',
    action: 'bloquear_temp',
    addedAt: '2026-04-18T15:45:00Z',
    addedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'bw-13',
    word: 'n****',
    level: 'grave',
    action: 'banear_ip',
    addedAt: '2026-05-02T09:00:00Z',
    addedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'bw-14',
    word: 'basura',
    level: 'leve',
    action: 'ocultar',
    addedAt: '2026-05-20T14:00:00Z',
    addedBy: 'Diego Ramírez',
    active: true,
  },
  {
    id: 'bw-15',
    word: 'porquería',
    level: 'leve',
    action: 'ocultar',
    addedAt: '2026-06-01T07:45:00Z',
    addedBy: 'María Solís',
    active: true,
  },
];

export const bans: BanEntry[] = [
  {
    id: 'ban-1',
    username: 'TrollMaster99',
    ip: '186.32.45.12',
    reason: 'Insultos graves y acoso a otros oyentes durante el programa Tardes de Pop.',
    action: 'bloqueo_perm',
    date: '2026-04-12T16:30:00Z',
    duration: 'Permanente',
    bannedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'ban-2',
    username: 'SpamBot_CR',
    ip: '201.198.67.34',
    reason: 'Spam masivo con links de phishing en el chat de 3 programas distintos.',
    action: 'baneo_ip',
    date: '2026-03-28T11:15:00Z',
    duration: 'Permanente',
    bannedBy: 'Diego Ramírez',
    active: true,
  },
  {
    id: 'ban-3',
    username: 'ElToxico506',
    ip: '190.122.89.55',
    reason: 'Lenguaje ofensivo y amenazas contra la locutora Natalia Vargas.',
    action: 'bloqueo_perm',
    date: '2026-05-01T20:00:00Z',
    duration: 'Permanente',
    bannedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'ban-4',
    username: 'DJ_Falso_7',
    ip: '181.44.23.88',
    reason: 'Suplantación de identidad de un locutor de la radio.',
    action: 'bloqueo_temp',
    date: '2026-05-18T14:50:00Z',
    duration: '30 días',
    bannedBy: 'María Solís',
    active: true,
  },
  {
    id: 'ban-5',
    username: 'MusicLover23',
    ip: '200.90.12.45',
    reason: 'Mensajes repetidos solicitando la misma canción 12 veces seguidas.',
    action: 'bloqueo_temp',
    date: '2026-05-25T09:20:00Z',
    duration: '24 horas',
    bannedBy: 'Diego Ramírez',
    active: true,
  },
  {
    id: 'ban-6',
    username: 'RadioFan506',
    ip: '190.211.34.78',
    reason: 'Publicidad no autorizada de una radio competidora.',
    action: 'bloqueo_temp',
    date: '2026-06-02T18:10:00Z',
    duration: '7 días',
    bannedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'ban-7',
    username: 'Xx_Hater_xX',
    ip: '186.45.67.90',
    reason: 'Insultos racistas durante el especial de música afrocaribeña.',
    action: 'baneo_ip',
    date: '2026-01-20T22:30:00Z',
    duration: 'Permanente',
    bannedBy: 'Carlos Mendoza',
    active: true,
  },
  {
    id: 'ban-8',
    username: 'AnonCR2024',
    ip: '201.188.56.12',
    reason: 'Comportamiento tóxico recurrente. Tercera advertencia.',
    action: 'bloqueo_temp',
    date: '2026-03-10T15:45:00Z',
    duration: '14 días',
    bannedBy: 'María Solís',
    active: false,
  },
];

export const blockedIPs: BlockedIP[] = [
  {
    id: 'ip-1',
    ip: '201.198.67.34',
    reason: 'Spam masivo con links maliciosos.',
    blockedAt: '2026-03-28T11:15:00Z',
    blockedBy: 'Diego Ramírez',
    notes: 'IP residencial de San José. Reincidente.',
    active: true,
  },
  {
    id: 'ip-2',
    ip: '186.45.67.90',
    reason: 'Insultos racistas y lenguaje de odio.',
    blockedAt: '2026-01-20T22:30:00Z',
    blockedBy: 'Carlos Mendoza',
    notes: 'Posible VPN. Geolocalización inconsistente.',
    active: true,
  },
  {
    id: 'ip-3',
    ip: '190.88.123.44',
    reason: 'Ataque de bots generando mensajes falsos.',
    blockedAt: '2026-02-14T03:00:00Z',
    blockedBy: 'Sistema',
    notes: 'Bloqueo automático por sistema de detección de bots.',
    active: true,
  },
  {
    id: 'ip-4',
    ip: '181.33.99.77',
    reason: 'Intentos repetidos de publicar links de apuestas.',
    blockedAt: '2026-05-30T21:15:00Z',
    blockedBy: 'Carlos Mendoza',
    notes: 'Casa de apuestas online. 5 intentos en 1 hora.',
    active: true,
  },
  {
    id: 'ip-5',
    ip: '200.122.44.18',
    reason: 'Suplantación de identidad del equipo de la radio.',
    blockedAt: '2026-04-05T16:40:00Z',
    blockedBy: 'María Solís',
    notes: 'Se hacía pasar por productor para estafar oyentes.',
    active: false,
  },
];

export const silencedUsers: SilencedUser[] = [
  {
    id: 'sil-1',
    username: 'Carlos_Troll_88',
    silencedAt: '2026-06-07T20:15:00Z',
    duration: '48 horas',
    silencedBy: 'Diego Ramírez',
    reason: 'Comentarios fuera de lugar durante entrevista en vivo.',
    active: true,
  },
  {
    id: 'sil-2',
    username: 'MaríaFanatica',
    silencedAt: '2026-06-07T19:30:00Z',
    duration: '12 horas',
    silencedBy: 'María Solís',
    reason: 'Envío excesivo de mensajes (flood) durante concurso.',
    active: true,
  },
  {
    id: 'sil-3',
    username: 'ElChismosoCR',
    silencedAt: '2026-06-06T14:00:00Z',
    duration: '6 horas',
    silencedBy: 'Carlos Mendoza',
    reason: 'Difundir información falsa sobre un locutor.',
    active: true,
  },
  {
    id: 'sil-4',
    username: 'DjNovato99',
    silencedAt: '2026-06-05T10:45:00Z',
    duration: '24 horas',
    silencedBy: 'Diego Ramírez',
    reason: 'Promocionar su propio stream durante programa ajeno.',
    active: false,
  },
];

export const moderationHistory: ModHistoryEntry[] = [
  {
    id: 'hist-1',
    timestamp: '2026-06-08T09:15:00Z',
    admin: 'Carlos Mendoza',
    action: 'Bloqueo permanente',
    target: 'ElToxico506',
    targetType: 'usuario',
    details: 'Lenguaje ofensivo reiterado. Tercera infracción grave.',
  },
  {
    id: 'hist-2',
    timestamp: '2026-06-08T08:42:00Z',
    admin: 'Sistema',
    action: 'Ocultar mensaje',
    target: 'mensaje #45892',
    targetType: 'mensaje',
    details: 'Filtro automático: palabra grave detectada. Mensaje ocultado.',
  },
  {
    id: 'hist-3',
    timestamp: '2026-06-07T22:10:00Z',
    admin: 'Diego Ramírez',
    action: 'Silenciar usuario',
    target: 'Carlos_Troll_88',
    targetType: 'usuario',
    details: 'Silenciado por 48 horas. Comentarios fuera de lugar durante entrevista.',
  },
  {
    id: 'hist-4',
    timestamp: '2026-06-07T21:33:00Z',
    admin: 'Sistema',
    action: 'Reemplazar mensaje',
    target: 'mensaje #45780',
    targetType: 'mensaje',
    details: 'Filtro automático: palabra media reemplazada con ***.',
  },
  {
    id: 'hist-5',
    timestamp: '2026-06-07T19:30:00Z',
    admin: 'María Solís',
    action: 'Silenciar usuario',
    target: 'MaríaFanatica',
    targetType: 'usuario',
    details: 'Silenciada por 12 horas. Flood de mensajes durante concurso.',
  },
  {
    id: 'hist-6',
    timestamp: '2026-06-07T16:55:00Z',
    admin: 'Carlos Mendoza',
    action: 'Agregar palabra',
    target: 'muérete (grave)',
    targetType: 'palabra',
    details: 'Palabra agregada al filtro. Acción: bloquear temporal.',
  },
  {
    id: 'hist-7',
    timestamp: '2026-06-06T18:20:00Z',
    admin: 'Sistema',
    action: 'Bloqueo automático',
    target: 'Usuario spam temporal',
    targetType: 'usuario',
    details: '3 advertencias excedidas. Bloqueo automático por 60 min.',
  },
  {
    id: 'hist-8',
    timestamp: '2026-06-06T14:00:00Z',
    admin: 'Carlos Mendoza',
    action: 'Silenciar usuario',
    target: 'ElChismosoCR',
    targetType: 'usuario',
    details: 'Silenciado por 6 horas. Difundir información falsa.',
  },
  {
    id: 'hist-9',
    timestamp: '2026-06-05T23:45:00Z',
    admin: 'Sistema',
    action: 'Bloqueo por links',
    target: 'IP 181.33.99.77',
    targetType: 'ip',
    details: 'Intento de publicar link de apuestas. IP bloqueada automáticamente.',
  },
  {
    id: 'hist-10',
    timestamp: '2026-06-05T10:45:00Z',
    admin: 'Diego Ramírez',
    action: 'Silenciar usuario',
    target: 'DjNovato99',
    targetType: 'usuario',
    details: 'Silenciado por 24 horas. Autopromoción en programa ajeno.',
  },
  {
    id: 'hist-11',
    timestamp: '2026-06-04T15:30:00Z',
    admin: 'María Solís',
    action: 'Desbloquear usuario',
    target: 'AnonCR2024',
    targetType: 'usuario',
    details: 'Bloqueo temporal cumplido. Usuario rehabilitado.',
  },
  {
    id: 'hist-12',
    timestamp: '2026-06-04T12:10:00Z',
    admin: 'Carlos Mendoza',
    action: 'Banear IP',
    target: '186.45.67.90',
    targetType: 'ip',
    details: 'IP baneada permanentemente. Insultos racistas.',
  },
  {
    id: 'hist-13',
    timestamp: '2026-06-03T09:00:00Z',
    admin: 'Sistema',
    action: 'Ocultar mensaje',
    target: 'mensaje #45210',
    targetType: 'mensaje',
    details: 'Filtro por mayúsculas excesivas. Mensaje ocultado.',
  },
  {
    id: 'hist-14',
    timestamp: '2026-06-02T20:45:00Z',
    admin: 'Diego Ramírez',
    action: 'Eliminar palabra',
    target: 'palabra obsoleta',
    targetType: 'palabra',
    details: 'Palabra removida del filtro por falsos positivos.',
  },
  {
    id: 'hist-15',
    timestamp: '2026-06-02T18:10:00Z',
    admin: 'Carlos Mendoza',
    action: 'Bloqueo temporal',
    target: 'RadioFan506',
    targetType: 'usuario',
    details: 'Bloqueado por 7 días. Publicidad no autorizada.',
  },
];

/* ─── Labels ─── */

export const wordLevelLabels: Record<WordLevel, string> = {
  leve: 'Leve',
  medio: 'Medio',
  grave: 'Grave',
};

export const wordLevelColors: Record<WordLevel, string> = {
  leve: 'bg-amber-100 text-amber-700',
  medio: 'bg-orange-100 text-orange-700',
  grave: 'bg-red-100 text-red-700',
};

export const wordActionLabels: Record<WordAction, string> = {
  ocultar: 'Ocultar mensaje',
  reemplazar: 'Reemplazar con ***',
  revision: 'Enviar a revisión',
  bloquear_temp: 'Bloquear temporal',
  banear_ip: 'Banear IP',
};

export const wordActionIcons: Record<WordAction, string> = {
  ocultar: 'ri-eye-off-line',
  reemplazar: 'ri-asterisk',
  revision: 'ri-file-search-line',
  bloquear_temp: 'ri-timer-line',
  banear_ip: 'ri-global-line',
};